"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUpRight, RefreshCw, Search } from "lucide-react";
import styles from "./liquidity-sweep.module.css";

type Row = { symbol: string; date: string; open: number; high: number; low: number; close: number; bounce_from_low_pct: number; sweep_depth_pct: number; swept_level: number; volume_ratio: number | null; status: string; green_candle: boolean };
type Result = { results: Row[]; total: number; truncated: boolean; latest_date: string | null; symbols_scanned: number; errors: { symbol: string }[] };
const fmt = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 2 });
const sessionLabel = (value: string | null) => value ? new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(new Date(`${value}T00:00:00Z`)) : "Unavailable";

function Candle({ row }: { row: Row }) {
  const top = Math.max(row.high, row.swept_level);
  const span = top - row.low;
  const y = (price: number) => 6 + (top - price) / span * 44;
  const color = row.green_candle ? "#34d399" : "#fb7185";
  return <svg width="58" height="56" viewBox="0 0 58 56" role="img" aria-label={`${row.symbol}: open ${row.open}, high ${row.high}, low ${row.low}, close ${row.close}`}>
    <line x1="5" x2="53" y1={y(row.swept_level)} y2={y(row.swept_level)} stroke="currentColor" strokeDasharray="3 3" opacity="0.35" />
    <line x1="29" x2="29" y1={y(row.high)} y2={y(row.low)} stroke={color} strokeWidth="2" />
    <rect x="23" y={y(Math.max(row.open, row.close))} width="12" height={Math.max(2, Math.abs(y(row.open) - y(row.close)))} fill={color} rx="1" />
  </svg>;
}

export default function LiquiditySweepClient() {
  const [data, setData] = useState<Result | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [bounce, setBounce] = useState("0");
  const [status, setStatus] = useState("all");

  async function load(signal?: AbortSignal) {
    setLoading(true); setError("");
    try {
      const response = await fetch("/api/scanner/liquidity-sweep?limit=2000", { cache: "no-store", signal });
      if (!response.ok) throw new Error("Unable to load sweeps. Please try again.");
      const result: Result = await response.json();
      if (!signal?.aborted) setData(result);
    } catch (e) {
      if (!signal?.aborted) setError(e instanceof Error ? e.message : "Unable to load sweeps");
    } finally { if (!signal?.aborted) setLoading(false); }
  }

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, []);

  const rows = useMemo(() => (data?.results ?? []).filter(row =>
    row.symbol.includes(search.trim().toUpperCase()) && row.bounce_from_low_pct >= Number(bounce) &&
    (status === "all" || (status === "reclaimed" ? row.status === "Reclaimed" : row.status !== "Reclaimed"))
  ).sort((a, b) => b.sweep_depth_pct - a.sweep_depth_pct), [data, search, bounce, status]);
  const reclaimed = data?.results.filter(row => row.status === "Reclaimed").length ?? 0;
  const largest = data?.results.length ? Math.max(...data.results.map(row => row.sweep_depth_pct)) : null;

  return <>
    <section className="page-header page-header--accent"><div className="page-header-container">
      <h1 className="page-title">Liquidity Sweep Scanner</h1>
      <p className="page-subtitle">Previous trading session’s downside sweeps, ranked by sweep percentage.</p>
    </div></section>
    <section className="strategy-detail-section"><div className="strategy-detail-container">
      <div className={styles.sessionBar}>
        <div><span className={styles.eyebrow}>Previous trading session</span><strong>{sessionLabel(data?.latest_date ?? null)}</strong></div>
        <button className={styles.refresh} onClick={() => void load()} disabled={loading}><RefreshCw size={15} className={loading ? styles.spinning : undefined} />{loading ? "Updating…" : "Refresh"}</button>
      </div>
      <div className={styles.stats}>
        <div className={styles.stat}><span>Sweep candles</span><strong>{data ? data.total : "—"}</strong><small>Across {data ? data.symbols_scanned : "—"} stocks</small></div>
        <div className={styles.stat}><span>Largest sweep</span><strong>{largest === null ? "—" : `${fmt(largest)}%`}</strong><small>Below the prior 5-session low</small></div>
        <div className={styles.stat}><span>Support reclaimed</span><strong>{data ? reclaimed : "—"}</strong><small>Closed back above the swept level</small></div>
      </div>
      <div className={styles.toolbar}>
        <label className={styles.search}><Search size={17} /><input aria-label="Search stock symbol" placeholder="Search symbol…" value={search} onChange={e => setSearch(e.target.value)} /></label>
        <label className={styles.filter}>Bounce from low<select value={bounce} onChange={e => setBounce(e.target.value)}><option value="0">Any bounce</option><option value="2">At least 2%</option><option value="4">At least 4%</option><option value="6">At least 6%</option></select></label>
        <label className={styles.filter}>Close position<select value={status} onChange={e => setStatus(e.target.value)}><option value="all">All sweeps</option><option value="reclaimed">Above swept level</option><option value="recovery">Below / at swept level</option></select></label>
        <span className={styles.count}>{rows.length} shown</span>
      </div>
      {error && <div className={styles.message} role="alert">{error}</div>}
      {loading && !data && <div className={styles.message} role="status">Loading previous-session sweeps…</div>}
      {data && !error && <>
        {data.errors.length > 0 && <p className={styles.note}>{data.errors.length} stock files could not be read.</p>}
        {data.truncated && <p className={styles.note}>Showing the largest {data.results.length} sweeps.</p>}
        <div className={styles.tableWrap} aria-busy={loading}><table className={`data-table ${styles.table}`}>
          <thead><tr><th>Stock</th><th>Candle</th><th aria-sort="descending">Sweep % <ArrowDown size={13} /></th><th>Bounce from low</th><th>Day low</th><th>Close</th><th>Swept level</th><th>Relative volume</th><th>Close position</th></tr></thead>
          <tbody>{rows.map(row => <tr key={`${row.symbol}:${row.date}`}>
            <td><Link className={styles.symbol} href={`/stock/${row.symbol}`}>{row.symbol}<ArrowUpRight size={13} /></Link></td>
            <td><Candle row={row} /></td>
            <td><span className={styles.sweep}>{fmt(row.sweep_depth_pct)}%</span></td>
            <td className={styles.bounce}>+{fmt(row.bounce_from_low_pct)}%</td>
            <td>{fmt(row.low)}</td><td>{fmt(row.close)}</td><td>{fmt(row.swept_level)}</td>
            <td>{row.volume_ratio == null ? "—" : `${fmt(row.volume_ratio)}×`}</td>
            <td><span className={`${styles.badge} ${row.status === "Reclaimed" ? styles.reclaimed : styles.recovery}`}>{row.status === "Reclaimed" ? "Above swept level" : "Below / at swept level"}</span></td>
          </tr>)}</tbody>
        </table>{!rows.length && <div className={styles.message}>{data.total ? "No sweeps match your filters." : "No sweep candles in the previous trading session."}</div>}</div>
      </>}
      <div className={styles.explainer}><p><strong>Sweep %</strong> — how far the day’s low fell below the prior 5-session low.</p><p><strong>Bounce from low</strong> — how much the price rose from the day’s low to its close.</p><p className={styles.note}>Dashed candle line marks the swept level. Results use the latest available session before today in Pakistan; weekends and holidays use the prior trading day.</p></div>
    </div></section>
  </>;
}
